import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ReportsSectionCompact.css";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import { useAuth } from "../context/AuthContext";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import {
  getSuperAdminSalonValuationConfig,
  getSuperAdminSalonValuationFilters,
  runSuperAdminSalonValuationReport,
  updateSuperAdminSalonValuationConfig,
} from "../api/superadminSalonValuationReport";
import { BTN_PRIMARY, FIELD_CLASS, LABEL_TEXT, MODAL_TEXT } from "../ui/modalStyles";

dayjs.extend(utc);
dayjs.extend(timezone);

const SCOPE_OPTIONS = [
  { value: "all", label: "All Salons" },
  { value: "business", label: "Business" },
  { value: "salon", label: "Salon" },
];

const HELP_SECTIONS = [
  {
    id: "snapshot",
    title: "Valuation Snapshot",
    description: "Estimated range, midpoint, and confidence at a glance.",
    anchorId: "sa-salon-valuation-snapshot",
  },
  {
    id: "drivers",
    title: "Key Drivers",
    description: "Revenue and SDE drivers that shape the valuation range.",
    anchorId: "sa-salon-valuation-drivers",
  },
  {
    id: "kpis",
    title: "KPI Evidence",
    description: "Operational KPI evidence used to support confidence.",
    anchorId: "sa-salon-valuation-kpis",
  },
  {
    id: "assumptions",
    title: "Assumptions & Inputs",
    description: "Stored valuation assumptions and financial inputs.",
    anchorId: "sa-salon-valuation-assumptions",
  },
  {
    id: "improve",
    title: "Improve Your Value",
    description: "Targeted actions that can improve value and confidence.",
    anchorId: "sa-salon-valuation-recommendations",
  },
];

const FIELD_TOOLTIPS = {
  scope:
    "Choose all salons, a single business, or one salon. Accounting dates use salon/business timezone rules.",
  business: "Required in Business scope. In Salon scope, choose the business first or pick a salon directly.",
  salon: "Required in Salon scope.",
  start_date: "First accounting date included in this report.",
  end_date: "Last accounting date included in this report.",
  run:
    "Runs valuation and KPI evidence for the selected scope and date range.",
  read_only_assumptions:
    "Assumptions are stored per salon; switch scope to Salon to edit.",
};

const NUMBER_FIELDS = [
  "payroll_percent",
  "rent_monthly",
  "supplies_percent",
  "merchant_fees_percent",
  "other_overhead_monthly",
  "owner_addbacks_annual",
  "one_time_expenses_annual",
  "equipment_value",
  "inventory_value",
  "leasehold_improvements_value",
  "sde_multiple_low",
  "sde_multiple_high",
  "revenue_multiple_low",
  "revenue_multiple_high",
  "lease_risk_adjustment_pct",
  "owner_dependence_adjustment_pct",
  "rebook_window_days",
];

const DEFAULT_CONFIG = {
  payroll_mode: "percent",
  payroll_percent: 45,
  rent_monthly: 0,
  supplies_percent: 8,
  merchant_fees_percent: 2.5,
  other_overhead_monthly: 0,
  owner_addbacks_annual: 0,
  one_time_expenses_annual: 0,
  equipment_value: 0,
  inventory_value: 0,
  leasehold_improvements_value: 0,
  sde_multiple_low: 2,
  sde_multiple_high: 3,
  revenue_multiple_low: 0.3,
  revenue_multiple_high: 0.8,
  lease_risk_adjustment_pct: 0,
  owner_dependence_adjustment_pct: 0,
  rebook_window_days: 30,
  notes: "",
};

const CARD_CLASS = "rounded-2xl border border-slate-200 bg-white p-5";

const formatMoney = (value) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "$0.00";
  const abs = Math.abs(num).toFixed(2);
  return num < 0 ? `-$${abs}` : `$${abs}`;
};

const formatPct = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "Not enough data yet";
  return `${num.toFixed(2)}%`;
};

const formatDropdownLabel = (entry, fallbackPrefix) => {
  const id = entry?.id;
  const name = entry?.name || fallbackPrefix;
  if (id === null || id === undefined || id === "") return name;
  return `${id} - ${name}`;
};

const confidenceBadgeClass = (label) => {
  if (label === "High") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (label === "Medium") return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-rose-100 text-rose-700 border-rose-200";
};

const toNumberOrUndefined = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

const prettifyFieldName = (value) =>
  String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

export default function SuperAdminSalonValuationReport() {
  const { is_super_admin, salonTimezone: authSalonTimezone } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const hasAutoRunRef = useRef(false);

  const [scope, setScope] = useState("all");
  const [businessId, setBusinessId] = useState("");
  const [salonId, setSalonId] = useState("");
  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [report, setReport] = useState(null);

  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filtersError, setFiltersError] = useState("");
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");

  const businessMap = useMemo(() => {
    const map = new Map();
    (businesses || []).forEach((entry) => {
      if (!entry) return;
      map.set(String(entry.id), entry);
    });
    return map;
  }, [businesses]);

  const salonMap = useMemo(() => {
    const map = new Map();
    (salons || []).forEach((entry) => {
      if (!entry) return;
      map.set(String(entry.id), entry);
    });
    return map;
  }, [salons]);

  const filteredSalons = useMemo(() => {
    if (!businessId) return salons;
    return (salons || []).filter(
      (entry) => String(entry.business_id || "") === String(businessId)
    );
  }, [salons, businessId]);

  const anchorTimezone = useMemo(() => {
    if (scope === "salon" && salonId) {
      const selectedSalon = salonMap.get(String(salonId));
      return selectedSalon?.timezone || authSalonTimezone || "UTC";
    }
    if (scope === "business" && businessId) {
      const selectedBusiness = businessMap.get(String(businessId));
      return selectedBusiness?.timezone || authSalonTimezone || "UTC";
    }
    return authSalonTimezone || "UTC";
  }, [scope, salonId, salonMap, businessId, businessMap, authSalonTimezone]);

  useEffect(() => {
    if (!anchorTimezone) return;
    const now = dayjs().tz(anchorTimezone);
    setEndDate(now.format("YYYY-MM-DD"));
    setStartDate(now.subtract(12, "month").add(1, "day").format("YYYY-MM-DD"));
    hasAutoRunRef.current = false;
  }, [anchorTimezone, scope]);

  useEffect(() => {
    if (!salonId || !businessId) return;
    const selectedSalon = salonMap.get(String(salonId));
    if (!selectedSalon) {
      setSalonId("");
      return;
    }
    if (String(selectedSalon.business_id || "") !== String(businessId)) {
      setSalonId("");
    }
  }, [businessId, salonId, salonMap]);

  const loadFilters = useCallback(async () => {
    if (!is_super_admin) return;
    setFiltersLoading(true);
    setFiltersError("");
    try {
      const data = await getSuperAdminSalonValuationFilters({
        scope,
        business_id: toNumberOrUndefined(businessId),
        salon_id: toNumberOrUndefined(salonId),
      });
      setBusinesses(Array.isArray(data?.businesses) ? data.businesses : []);
      setSalons(Array.isArray(data?.salons) ? data.salons : []);
    } catch (err) {
      setSalons([]);
      setFiltersError(err?.message || "Unable to load report filters.");
    } finally {
      setFiltersLoading(false);
    }
  }, [is_super_admin, scope, businessId, salonId]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  const loadSalonConfig = useCallback(async () => {
    if (!is_super_admin || scope !== "salon" || !salonId) return;
    setLoadingConfig(true);
    setError("");
    try {
      const data = await getSuperAdminSalonValuationConfig(Number(salonId));
      setConfig({ ...DEFAULT_CONFIG, ...(data || {}) });
    } catch (err) {
      setError(err?.message || "Unable to load valuation assumptions.");
    } finally {
      setLoadingConfig(false);
    }
  }, [is_super_admin, scope, salonId]);

  useEffect(() => {
    if (scope !== "salon") return;
    loadSalonConfig();
  }, [scope, salonId, loadSalonConfig]);

  const dateRangeError = useMemo(() => {
    if (!startDate || !endDate) return "Start and end dates are required.";
    return endDate < startDate ? "End date cannot be before start date." : "";
  }, [startDate, endDate]);

  const canEditAssumptions = scope === "salon" && Boolean(salonId);
  const canRun = useMemo(() => {
    if (dateRangeError) return false;
    if (scope === "business" && businessId && filteredSalons.length === 0) return false;
    if (scope === "salon" && !salonId) return false;
    return Boolean(startDate && endDate);
  }, [dateRangeError, scope, businessId, filteredSalons.length, salonId, startDate, endDate]);

  const payload = useMemo(
    () => {
      const selectedBusinessId = toNumberOrUndefined(businessId);
      return {
        // In Business scope, "All businesses" should run aggregate results
        // across all salons, excluding businesses with no salons.
        scope: scope === "business" && !selectedBusinessId ? "all" : scope,
        business_id: selectedBusinessId,
        salon_id: toNumberOrUndefined(salonId),
        start_date: startDate,
        end_date: endDate,
      };
    },
    [scope, businessId, salonId, startDate, endDate]
  );

  const runReport = useCallback(
    async (payloadOverride = null) => {
      if (!canRun) {
        setError(
          dateRangeError ||
            (scope === "business" && businessId && filteredSalons.length === 0
              ? "No salons found for selected business."
              : "") ||
            (scope === "salon"
                ? "Choose a salon for Salon scope."
                : "Complete required filters.")
        );
        return;
      }
      setRunning(true);
      setError("");
      try {
        const data = await runSuperAdminSalonValuationReport(payloadOverride || payload);
        setReport(data || null);
        if (scope !== "salon") {
          setConfig((prev) => ({
            ...DEFAULT_CONFIG,
            ...(prev || {}),
            ...(data?.assumptions_used || {}),
          }));
        }
      } catch (err) {
        setReport(null);
        setError(err?.message || "Unable to run estimated salon value report.");
      } finally {
        setRunning(false);
      }
    },
    [canRun, dateRangeError, scope, businessId, filteredSalons.length, payload]
  );

  useEffect(() => {
    if (!is_super_admin || hasAutoRunRef.current) return;
    if (filtersLoading) return;
    if (!canRun) return;
    if (scope === "salon" && loadingConfig) return;
    hasAutoRunRef.current = true;
    runReport(payload);
  }, [is_super_admin, filtersLoading, canRun, scope, loadingConfig, runReport, payload]);

  const updateField = (field, value) => {
    setConfig((prev) => ({ ...(prev || {}), [field]: value }));
    setSaveMessage("");
  };

  const handleSave = async () => {
    if (!canEditAssumptions || !config) return;
    setSaving(true);
    setError("");
    setSaveMessage("");
    try {
      const savePayload = { ...config };
      NUMBER_FIELDS.forEach((field) => {
        const parsed = Number(savePayload[field]);
        savePayload[field] = Number.isFinite(parsed) ? parsed : 0;
      });
      savePayload.rebook_window_days = Math.max(
        1,
        Math.floor(Number(savePayload.rebook_window_days || 30))
      );
      const saved = await updateSuperAdminSalonValuationConfig(
        Number(salonId),
        savePayload
      );
      setConfig({ ...DEFAULT_CONFIG, ...(saved || savePayload) });
      setSaveMessage("Assumptions saved.");
      await runReport();
    } catch (err) {
      setError(err?.message || "Unable to save assumptions.");
    } finally {
      setSaving(false);
    }
  };

  const handleScopeChange = (value) => {
    setScope(value);
    setError("");
    hasAutoRunRef.current = false;
    if (value === "all") {
      setSalonId("");
    }
    if (value === "business") {
      setSalonId("");
    }
  };

  const handleBusinessChange = (value) => {
    setBusinessId(value);
    setError("");
    hasAutoRunRef.current = false;
    if (!value && scope !== "salon") {
      setSalonId("");
    }
    if (!salonId) return;
    const selectedSalon = salonMap.get(String(salonId));
    if (
      selectedSalon &&
      value &&
      String(selectedSalon.business_id || "") !== String(value)
    ) {
      setSalonId("");
    }
  };

  const handleSalonChange = (value) => {
    setSalonId(value);
    setError("");
    hasAutoRunRef.current = false;
    if (!value) return;
    const selectedSalon = salonMap.get(String(value));
    if (selectedSalon?.business_id) {
      setBusinessId(String(selectedSalon.business_id));
    }
  };

  const valuation = report?.valuation_snapshot || {};
  const keyDrivers = report?.key_drivers || {};
  const kpis = report?.kpis || {};
  const sanity = report?.sanity_checks || {};
  const notes = report?.kpi_notes || {};
  const notices = Array.isArray(report?.notices) ? report.notices : [];

  const timezoneLine = useMemo(() => {
    if (scope === "salon") {
      const tz =
        report?.date_range?.tz_name ||
        salonMap.get(String(salonId))?.timezone ||
        anchorTimezone;
      return `Salon timezone: ${tz || "UTC"}`;
    }
    if (scope === "business") {
      const tz =
        report?.date_range?.tz_name ||
        businessMap.get(String(businessId))?.timezone ||
        anchorTimezone;
      return `Business timezone: ${tz || "UTC"}`;
    }
    if ((report?.date_range?.tz_name || "").toLowerCase() === "per-salon") {
      return "Accounting dates: per-salon timezone.";
    }
    return `Timezone: ${report?.date_range?.tz_name || anchorTimezone || "UTC"}`;
  }, [
    scope,
    report?.date_range?.tz_name,
    salonMap,
    salonId,
    businessMap,
    businessId,
    anchorTimezone,
  ]);

  const noSalonSetupForBusiness = useMemo(
    () =>
      scope === "business" &&
      Boolean(businessId) &&
      !filtersLoading &&
      !filtersError &&
      filteredSalons.length === 0,
    [scope, businessId, filtersLoading, filtersError, filteredSalons.length]
  );

  useEffect(() => {
    if (!noSalonSetupForBusiness) return;
    setReport(null);
  }, [noSalonSetupForBusiness]);

  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip text={FIELD_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader
            title="Estimated Salon Value"
            closeTo="/superadmin/reports"
          />

          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div className="rounded-2xl">
              <ReportHelpCard
                title="Help Guide: Estimated Salon Value"
                subtitle="Review valuation range, confidence, and assumptions across Super Admin scope."
                sections={HELP_SECTIONS}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            <div className={`${CARD_CLASS} space-y-4`}>
              <div className="grid gap-4 lg:grid-cols-6">
                <div>
                  {renderFieldLabel("Scope", "scope")}
                  <select
                    className={`w-full mt-1 ${FIELD_CLASS}`}
                    value={scope}
                    onChange={(event) => handleScopeChange(event.target.value)}
                    disabled={filtersLoading}
                  >
                    {SCOPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Business", "business")}
                  <select
                    className={`w-full mt-1 ${FIELD_CLASS}`}
                    value={businessId}
                    onChange={(event) => handleBusinessChange(event.target.value)}
                    disabled={filtersLoading || !(scope === "business" || scope === "salon")}
                  >
                    <option value="">All businesses</option>
                    {businesses.map((entry) => (
                      <option key={entry.id} value={String(entry.id)}>
                        {formatDropdownLabel(entry, "Business")}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Salon", "salon")}
                  <select
                    className={`w-full mt-1 ${FIELD_CLASS}`}
                    value={salonId}
                    onChange={(event) => handleSalonChange(event.target.value)}
                    disabled={filtersLoading || scope !== "salon"}
                  >
                    <option value="">Select salon</option>
                    {filteredSalons.map((entry) => (
                      <option key={entry.id} value={String(entry.id)}>
                        {formatDropdownLabel(entry, "Salon")}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Start Date", "start_date")}
                  <input
                    type="date"
                    className={`w-full mt-1 ${FIELD_CLASS}`}
                    value={startDate}
                    onChange={(event) => {
                      setStartDate(event.target.value);
                      setError("");
                      hasAutoRunRef.current = false;
                    }}
                  />
                </div>
                <div>
                  {renderFieldLabel("End Date", "end_date")}
                  <input
                    type="date"
                    className={`w-full mt-1 ${FIELD_CLASS}`}
                    value={endDate}
                    onChange={(event) => {
                      setEndDate(event.target.value);
                      setError("");
                      hasAutoRunRef.current = false;
                    }}
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    className={`w-full ${BTN_PRIMARY}`}
                    onClick={() => runReport()}
                    disabled={running || filtersLoading || !canRun}
                  >
                    {running ? "Running..." : "Run Report"}
                  </button>
                </div>
              </div>
              <div className="text-sm text-slate-500">{timezoneLine}</div>
            </div>

            {filtersError ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {filtersError}
              </div>
            ) : null}
            {noSalonSetupForBusiness ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
                No salon is set up for this business, so an estimated salon value cannot be provided.
              </div>
            ) : null}
            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            ) : null}
            {saveMessage ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
                {saveMessage}
              </div>
            ) : null}
            {notices.map((item) => (
              <div key={item} className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
                {item}
              </div>
            ))}

            <div
              id="sa-salon-valuation-snapshot"
              className={`${CARD_CLASS} ${getSectionClassName("sa-salon-valuation-snapshot")}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Valuation Snapshot
                </h2>
                <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${confidenceBadgeClass(valuation.confidence_label)}`}>
                  {valuation.confidence_label || "Low"} confidence
                </span>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
                    Estimated Value Range
                    <InfoTooltip text="Primary output based on SDE multiple range with adjustments." label="Estimated Value Range" />
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatMoney(valuation.estimated_low)} - {formatMoney(valuation.estimated_high)}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
                    Best Estimate
                    <InfoTooltip text="Midpoint of the valuation range." label="Best Estimate" />
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatMoney(valuation.midpoint)}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
                    Confidence Score
                    <InfoTooltip text="0-100 score based on data verification, completeness, stability, and transparency." label="Confidence Score" />
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {Number(valuation.confidence_score || 0)}
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-slate-100">
                    <div
                      className="h-2 rounded-full bg-rose-500"
                      style={{
                        width: `${Math.max(
                          0,
                          Math.min(100, Number(valuation.confidence_score || 0))
                        )}%`,
                      }}
                    />
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    {valuation.method || "Income Approach (SDE multiple)"}
                  </div>
                </div>
              </div>
            </div>

            <div
              id="sa-salon-valuation-drivers"
              className={`${CARD_CLASS} ${getSectionClassName("sa-salon-valuation-drivers")}`}
            >
              <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-4">
                Key Drivers
              </h2>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
                <div className="rounded-xl border border-slate-200 p-3"><div className="text-xs uppercase text-slate-500">T12 Revenue</div><div className="mt-1 font-semibold text-slate-900">{formatMoney(keyDrivers.t12_revenue)}</div></div>
                <div className="rounded-xl border border-slate-200 p-3"><div className="text-xs uppercase text-slate-500">T12 SDE</div><div className="mt-1 font-semibold text-slate-900">{formatMoney(keyDrivers.t12_sde)}</div></div>
                <div className="rounded-xl border border-slate-200 p-3"><div className="text-xs uppercase text-slate-500">SDE Multiple</div><div className="mt-1 font-semibold text-slate-900">{Number(keyDrivers.sde_multiple_low || 0).toFixed(2)}x - {Number(keyDrivers.sde_multiple_high || 0).toFixed(2)}x</div></div>
                <div className="rounded-xl border border-slate-200 p-3"><div className="text-xs uppercase text-slate-500">Add-backs Annual</div><div className="mt-1 font-semibold text-slate-900">{formatMoney(keyDrivers?.addbacks?.owner_addbacks_annual || 0)} + {formatMoney(keyDrivers?.addbacks?.one_time_expenses_annual || 0)}</div></div>
                <div className="rounded-xl border border-slate-200 p-3"><div className="text-xs uppercase text-slate-500">Adjustments</div><div className="mt-1 font-semibold text-slate-900">{formatPct(keyDrivers?.adjustments?.lease_risk_adjustment_pct || 0)} / {formatPct(keyDrivers?.adjustments?.owner_dependence_adjustment_pct || 0)}</div></div>
              </div>
            </div>

            <div
              id="sa-salon-valuation-kpis"
              className={`${CARD_CLASS} ${getSectionClassName("sa-salon-valuation-kpis")}`}
            >
              <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-4">KPI Evidence</h2>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {[
                  ["Repeat rate", kpis.repeat_rate, "repeat_rate"],
                  ["Rebooking rate", kpis.rebook_rate, "rebook_rate"],
                  ["Avg ticket", kpis.avg_ticket, "avg_ticket"],
                  ["Utilization", kpis.utilization_pct, "utilization_pct"],
                  ["No-show rate", kpis.no_show_rate, "no_show_rate"],
                  ["Cancellation rate", kpis.cancellation_rate, "cancellation_rate"],
                  ["Discount rate", kpis.discount_rate, "discount_rate"],
                ].map(([label, value, key]) => (
                  <div key={key} className="rounded-xl border border-slate-200 p-3">
                    <div className="text-xs uppercase text-slate-500 flex items-center gap-2">
                      {label}
                      {notes[key] ? <InfoTooltip text={notes[key]} label={String(label)} /> : null}
                    </div>
                    <div className="mt-1 font-semibold text-slate-900">
                      {key === "avg_ticket"
                        ? Number.isFinite(Number(value))
                          ? formatMoney(value)
                          : "Not enough data yet"
                        : formatPct(value)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-xl border border-slate-200 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500">Service mix (Top 5)</div>
                <ul className="mt-2 space-y-1 text-sm text-slate-700">
                  {(kpis.service_mix_top || []).length === 0 ? <li>Not enough data yet</li> : null}
                  {(kpis.service_mix_top || []).map((row) => (
                    <li key={`${row.service_name}-${row.category_name || ""}`} className="flex items-center justify-between">
                      <span>{row.service_name}{row.category_name ? ` (${row.category_name})` : ""}</span>
                      <span className="font-semibold text-slate-900">{formatMoney(row.net_sales)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div
              id="sa-salon-valuation-assumptions"
              className={`${CARD_CLASS} ${getSectionClassName("sa-salon-valuation-assumptions")}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">Assumptions & Inputs</h2>
                <div className="flex items-center gap-2">
                  {!canEditAssumptions ? (
                    <InfoTooltip text={FIELD_TOOLTIPS.read_only_assumptions} label="Assumptions" />
                  ) : null}
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!canEditAssumptions || saving || loadingConfig || !config}
                    className={BTN_PRIMARY}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
              {!canEditAssumptions ? (
                <p className="mb-3 text-sm text-slate-500">
                  Assumptions are read-only in this scope. Switch to <span className="font-semibold">Salon</span> scope to edit and save.
                </p>
              ) : null}
              {loadingConfig ? (
                <div className="text-slate-500">Loading assumptions...</div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  <label className="text-sm text-slate-700">Payroll mode<select className={`w-full mt-1 ${FIELD_CLASS}`} value={config?.payroll_mode || "percent"} onChange={(event) => updateField("payroll_mode", event.target.value)} disabled={!canEditAssumptions}><option value="percent">Percent</option><option value="payroll_data">Payroll Data</option></select></label>
                  {NUMBER_FIELDS.map((field) => (
                    <label key={field} className="text-sm text-slate-700">{prettifyFieldName(field)}<input type="number" className={`w-full mt-1 ${FIELD_CLASS}`} value={config?.[field] ?? ""} onChange={(event) => updateField(field, event.target.value)} disabled={!canEditAssumptions} /></label>
                  ))}
                  <label className="text-sm text-slate-700 md:col-span-2 lg:col-span-3">Notes<textarea className={`w-full mt-1 ${FIELD_CLASS}`} rows={3} value={config?.notes || ""} onChange={(event) => updateField("notes", event.target.value)} disabled={!canEditAssumptions} /></label>
                </div>
              )}
            </div>

            <div className={CARD_CLASS}>
              <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-2">Sanity Checks</h2>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200 p-3"><div className="text-xs uppercase text-slate-500">Revenue multiple range</div><div className="mt-1 font-semibold text-slate-900">{formatMoney(sanity.revenue_multiple_low)} - {formatMoney(sanity.revenue_multiple_high)}</div></div>
                <div className="rounded-xl border border-slate-200 p-3"><div className="text-xs uppercase text-slate-500">Asset floor</div><div className="mt-1 font-semibold text-slate-900">{formatMoney(sanity.asset_floor)}</div></div>
              </div>
              <p className="mt-3 text-xs text-slate-500">Used as secondary checks. Primary output is the SDE multiple range.</p>
            </div>

            <div
              id="sa-salon-valuation-recommendations"
              className={`${CARD_CLASS} ${getSectionClassName("sa-salon-valuation-recommendations")}`}
            >
              <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-3">Improve Your Value</h2>
              <ul className="space-y-2">
                {(report?.recommendations || []).length === 0 ? <li className="text-slate-500">No recommendations yet.</li> : null}
                {(report?.recommendations || []).map((item, idx) => (
                  <li key={`${item.title}-${idx}`} className="rounded-xl border border-slate-200 p-3">
                    <div className="font-semibold text-slate-900">{item.title}</div>
                    <div className="text-sm text-slate-600 mt-1">{item.impact_hint}</div>
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-600">
                      {item.metric_before ? <span className="rounded-full bg-slate-100 px-2 py-1">Current: {item.metric_before}</span> : null}
                      {item.target ? <span className="rounded-full bg-rose-50 px-2 py-1 text-rose-700">Target: {item.target}</span> : null}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
